/**
 * The search box, in the Symbol column's slot of the filter row.
 *
 * Mounted by AG Grid as that column's **floating filter**, because that is the
 * one row under the headers AG Grid already lays out column by column — pinned,
 * scrolled and resized with the column above it for free. It is a floating
 * filter in position only: it never touches the column's own filter (the
 * parent-filter callbacks go unused), so nothing typed here reaches
 * `getFilterModel()`. See `lib/symbol-search.ts` for why that matters.
 *
 * ## Open and closed without touching a column definition
 *
 * The row always exists — `floatingFilter` is set on the Symbol column once and
 * never toggled, because a changed column definition is a column rebuild. What
 * opens and closes is the **row's height** (`floatingFiltersHeight`, 0 when
 * closed) and this box, which renders nothing while closed. So showing the row
 * moves no column, loses no width and drops no sort.
 *
 * ## Escape, and where the cursor goes
 *
 * AG Grid's own floating-filter cell answers Escape inside its input by moving
 * focus to the cell around it, and it gets the key first (its listener is on the
 * header row; ours is React's, at the root). So when Escape only *clears*, the
 * cursor is put straight back in the box — left on the cell, the next letter
 * would reach the app's type-to-search and open the symbol dialog over a search
 * the user is still typing into. When Escape *closes*, focus goes to the Symbol
 * header, which is where the 🔍 that opened it lives.
 *
 * Typed letters are safe on the way through: the target is an `INPUT`, which the
 * host's type-to-search and digit keys both leave alone, and a key in the header
 * never reaches AG Grid's cell handling — so it cannot start the Add row either.
 */

import { useLayoutEffect, useRef, useSyncExternalStore, type CSSProperties } from "react";
import type { CustomFloatingFilterProps } from "ag-grid-react";

import type { SymbolSearch } from "../lib/symbol-search.js";

export interface SymbolSearchBoxParams {
  symbolSearch: SymbolSearch;
}

export function SymbolSearchBox(props: CustomFloatingFilterProps & SymbolSearchBoxParams) {
  const search = props.symbolSearch;
  const state = useSyncExternalStore(search.subscribe, search.get);
  const input = useRef<HTMLInputElement>(null);

  // On opening, and on every later "put the cursor back" — the 🔍 pressed while
  // text is in the box, or the × — which is what `focus` counts.
  useLayoutEffect(() => {
    if (state.open) input.current?.focus();
  }, [state.open, state.focus]);

  if (!state.open) return null;

  return (
    <div style={WRAP}>
      <input
        ref={input}
        value={state.text}
        aria-label="Search symbols"
        placeholder="Symbol or name"
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => search.setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          event.stopPropagation();
          const closing = !search.get().text;
          search.escape();
          if (closing) props.api.setFocusedHeader(props.column);
          else input.current?.focus();
        }}
        style={INPUT}
      />
      {state.text ? (
        <button
          type="button"
          aria-label="Clear search"
          title="Clear"
          // Keeps the cursor in the box through the press.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => search.clear()}
          style={CLEAR}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}

const WRAP: CSSProperties = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: "100%",
  gap: 2,
};

const INPUT: CSSProperties = {
  flex: "1 1 auto",
  minWidth: 0,
  height: "calc(100% - 8px)",
  boxSizing: "border-box",
  padding: "0 0.35rem",
  font: "inherit",
  fontSize: "0.78rem",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--qf-accent)",
  borderRadius: 3,
  outline: "none",
};

const CLEAR: CSSProperties = {
  flex: "0 0 auto",
  padding: "0 0.25rem",
  border: "none",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  font: "inherit",
  fontSize: "0.95rem",
  lineHeight: 1,
};
